import type { BuildingGraph } from "building-types/building/buildingGraphTypes";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import { isAbsoluteVerticalDatum, type DatumEntity } from "building-types/datums/datumEntityTypes";
import { resolveWallPlaneHorizontalDatumU } from "building-types/datums/resolveWallPlaneHorizontalDatum";
import type { ForeignEntityId } from "building-types/handles/foreignEntityId";
import type { Table } from "common/data/table";
import type { AABB2 } from "geom/aabb2Types";
import { DIST_EPSILON } from "geom/core";
import { planarTopologyDatumBoundsHasDatums, type PlanarTopologyDatumBounds } from "./planarTopologyDatumBoundsTypes";
import type {
  WallPlaneOperationAABB,
  WallPlaneOperationAABBHorizontalSideValue,
  WallPlaneOperationAABBRelativeSideValue,
  WallPlaneOperationAABBVerticalSideValue,
  WallPlaneOperationBoundary,
} from "./wallPlane";
import { getWallPlaneOperationAABB } from "./wallPlane";

function buildVerticalDatumSideValue(
  datumId: ForeignEntityId<"DATUM">,
  datums: Table<DatumEntity>,
): WallPlaneOperationAABBVerticalSideValue | undefined {
  const datum = datums[datumId];
  if (isAbsoluteVerticalDatum(datum)) {
    return { mode: "DATUM", datumId };
  }
  return undefined;
}

export function buildSplitRegionOperationAABB(
  bounds: PlanarTopologyDatumBounds,
  datums: Table<DatumEntity> = {},
): WallPlaneOperationAABB | undefined {
  if (!planarTopologyDatumBoundsHasDatums(bounds)) {
    return undefined;
  }

  return {
    start: bounds.startHorizontalDatumId
      ? { mode: "HORIZONTAL_DATUM", horizontalDatumId: bounds.startHorizontalDatumId }
      : undefined,
    end: bounds.endHorizontalDatumId
      ? { mode: "HORIZONTAL_DATUM", horizontalDatumId: bounds.endHorizontalDatumId }
      : undefined,
    bottom: bounds.startVerticalDatumId ? buildVerticalDatumSideValue(bounds.startVerticalDatumId, datums) : undefined,
    top: bounds.endVerticalDatumId ? buildVerticalDatumSideValue(bounds.endVerticalDatumId, datums) : undefined,
  };
}

export function buildSplitRegionOperationBoundary(
  bounds: PlanarTopologyDatumBounds,
  datums: Table<DatumEntity> = {},
): WallPlaneOperationBoundary | undefined {
  const aabb = buildSplitRegionOperationAABB(bounds, datums);
  if (!aabb) {
    return undefined;
  }
  return { boundaryType: "AABB", aabb };
}

type WallPlaneOperationAABBSideValue =
  | WallPlaneOperationAABBHorizontalSideValue
  | WallPlaneOperationAABBVerticalSideValue;

function operationAABBSideValuesMatch(
  ruleSide: WallPlaneOperationAABBSideValue | undefined,
  boundsSide: WallPlaneOperationAABBSideValue | undefined,
  axisHasDatumBounds: boolean,
): boolean {
  if (ruleSide === undefined && boundsSide === undefined) {
    return true;
  }
  if (boundsSide === undefined) {
    if (!axisHasDatumBounds) {
      return ruleSide === undefined;
    }
    if (ruleSide === undefined) {
      return true;
    }
    return ruleSide.mode === "RELATIVE" && Math.abs(ruleSide.value) <= DIST_EPSILON;
  }
  if (ruleSide === undefined) {
    return false;
  }
  if (ruleSide.mode !== boundsSide.mode) {
    return false;
  }
  switch (ruleSide.mode) {
    case "RELATIVE":
      return boundsSide.mode === "RELATIVE" && Math.abs(ruleSide.value - boundsSide.value) <= DIST_EPSILON;
    case "DATUM":
      return boundsSide.mode === "DATUM" && ruleSide.datumId === boundsSide.datumId;
    case "HORIZONTAL_DATUM":
      return boundsSide.mode === "HORIZONTAL_DATUM" && ruleSide.horizontalDatumId === boundsSide.horizontalDatumId;
    default:
      return false;
  }
}

export function operationAABBMatchesDatumBounds(
  operationBoundary: WallPlaneOperationBoundary | undefined,
  bounds: PlanarTopologyDatumBounds,
  datums: Table<DatumEntity> = {},
): boolean {
  const expectedAabb = buildSplitRegionOperationAABB(bounds, datums);
  if (!expectedAabb) {
    return operationBoundary === undefined;
  }

  const ruleAabb = operationBoundary ? getWallPlaneOperationAABB(operationBoundary) : undefined;
  if (!ruleAabb) {
    return false;
  }

  const hasHorizontalDatumBounds =
    bounds.startHorizontalDatumId !== undefined || bounds.endHorizontalDatumId !== undefined;
  const hasVerticalDatumBounds = bounds.startVerticalDatumId !== undefined || bounds.endVerticalDatumId !== undefined;

  return (
    operationAABBSideValuesMatch(ruleAabb.start, expectedAabb.start, hasHorizontalDatumBounds) &&
    operationAABBSideValuesMatch(ruleAabb.end, expectedAabb.end, hasHorizontalDatumBounds) &&
    operationAABBSideValuesMatch(ruleAabb.bottom, expectedAabb.bottom, hasVerticalDatumBounds) &&
    operationAABBSideValuesMatch(ruleAabb.top, expectedAabb.top, hasVerticalDatumBounds)
  );
}

function isWallPlaneOperationAABBSideOpen(side: WallPlaneOperationAABBSideValue | undefined): boolean {
  return side === undefined || (side.mode === "RELATIVE" && Math.abs(side.value) <= DIST_EPSILON);
}

/** True when an operation AABB has no datum-bound sides (full topology extent). */
export function isWallPlaneOperationAABBFullyOpen(aabb: WallPlaneOperationAABB): boolean {
  return (
    isWallPlaneOperationAABBSideOpen(aabb.start) &&
    isWallPlaneOperationAABBSideOpen(aabb.end) &&
    isWallPlaneOperationAABBSideOpen(aabb.bottom) &&
    isWallPlaneOperationAABBSideOpen(aabb.top)
  );
}

export function isWallPlaneOperationAABBRelativeHorizontalSide(
  side: WallPlaneOperationAABBHorizontalSideValue | undefined,
): side is WallPlaneOperationAABBRelativeSideValue {
  return side?.mode === "RELATIVE";
}

export function resolveWallPlaneOperationAABBHorizontalSide(
  side: WallPlaneOperationAABBHorizontalSideValue,
  planeHash: string,
  selectedAABB: AABB2,
  datumAbstractions: Table<DatumAbstraction>,
  buildingGraph: Pick<BuildingGraph, "buildingPlanes">,
  isStart: boolean,
): number | undefined {
  if (side.mode === "RELATIVE") {
    return isStart ? selectedAABB.minX - side.value : selectedAABB.maxX + side.value;
  }
  return resolveWallPlaneHorizontalDatumU(side.horizontalDatumId, planeHash, datumAbstractions, buildingGraph);
}

function resolveWallPlaneOperationAABBVerticalSide(
  sideValue: WallPlaneOperationAABBVerticalSideValue,
  selectedEdge: number,
  resolvedVerticalDatumZ: (datumId: string) => number | undefined,
  isBottom: boolean,
): number {
  if (sideValue.mode === "RELATIVE") {
    return isBottom ? selectedEdge - sideValue.value : selectedEdge + sideValue.value;
  }
  const resolved = resolvedVerticalDatumZ(sideValue.datumId);
  if (resolved === undefined) {
    return selectedEdge;
  }
  return resolved;
}

/**
 * Resolves operation AABB bounds in plane UV space against a selected domain outline.
 * Mirrors `WallPlaneDescriptorBuilder.resolveOperationDomainAABB`.
 */
export function resolveWallPlaneOperationAABBToUVAABB(
  operationAABB: WallPlaneOperationAABB,
  planeHash: string,
  selectedAABB: AABB2,
  datumAbstractions: Table<DatumAbstraction>,
  buildingGraph: Pick<BuildingGraph, "buildingPlanes">,
  resolvedVerticalDatumZ: (datumId: string) => number | undefined = () => undefined,
): AABB2 | null {
  let start = selectedAABB.minX;
  if (operationAABB.start !== undefined) {
    const resolvedStart = resolveWallPlaneOperationAABBHorizontalSide(
      operationAABB.start,
      planeHash,
      selectedAABB,
      datumAbstractions,
      buildingGraph,
      true,
    );
    if (resolvedStart !== undefined) {
      start = resolvedStart;
    }
  }

  let bottom = selectedAABB.minY;
  if (operationAABB.bottom !== undefined) {
    bottom = resolveWallPlaneOperationAABBVerticalSide(
      operationAABB.bottom,
      selectedAABB.minY,
      resolvedVerticalDatumZ,
      true,
    );
  }

  let end = selectedAABB.maxX;
  if (operationAABB.end !== undefined) {
    const resolvedEnd = resolveWallPlaneOperationAABBHorizontalSide(
      operationAABB.end,
      planeHash,
      selectedAABB,
      datumAbstractions,
      buildingGraph,
      false,
    );
    if (resolvedEnd !== undefined) {
      end = resolvedEnd;
    }
  }

  let top = selectedAABB.maxY;
  if (operationAABB.top !== undefined) {
    top = resolveWallPlaneOperationAABBVerticalSide(
      operationAABB.top,
      selectedAABB.maxY,
      resolvedVerticalDatumZ,
      false,
    );
  }

  if (start >= end || bottom >= top) {
    return null;
  }

  return { minX: start, minY: bottom, maxX: end, maxY: top };
}

/**
 * Convert a plane UV AABB into a `WallPlaneOperationAABB` expressed as insets relative to the
 * whole-plane outline AABB.
 */
export function planeUVAABBToOperationAABB(rect: AABB2, planeOutlineAABB: AABB2): WallPlaneOperationAABB {
  return {
    start: { mode: "RELATIVE", value: planeOutlineAABB.minX - rect.minX },
    end: { mode: "RELATIVE", value: rect.maxX - planeOutlineAABB.maxX },
    bottom: { mode: "RELATIVE", value: planeOutlineAABB.minY - rect.minY },
    top: { mode: "RELATIVE", value: rect.maxY - planeOutlineAABB.maxY },
  };
}
